// frontend/src/components/finance/Ledger.js
//
// The client-facing view onto the same Ledger that now powers the
// Dashboard's Net Profit/Liquid Cash figures (routes/dashboard.js,
// services/financialSummary.js) — structured to closely mirror
// components/admin/AuditLog.js on purpose, so a manager who already
// knows that screen finds this one familiar.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../api';
import { fmt, Pager } from '../common/CommonComponents';

const inputStyle = {
  padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8,
  fontSize: 12.5, color: "var(--text)", background: "var(--input-bg)", outline: "none",
};
const labelStyle = { display: "block", fontSize: 10.5, fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 };

const EMPTY_FILTERS = { dateFrom: "", dateTo: "", type: "", category: "", search: "" };
const hasCashImpact = entry => entry.cashImpact !== false;

// Human-readable label for each Ledger type enum value — kept here
// rather than in the backend response, same reasoning as AuditLog's
// inline action-badge styling: display concerns belong to the display
// layer, not the API.
const TYPE_LABELS = {
  CUSTOMER_PAYMENT: "Customer Payment",
  INVENTORY_PURCHASE: "Inventory Purchase",
  MATERIAL_COST: "Material Cost",
  MATERIAL_PROFIT: "Material Profit",
  LABOUR_COST: "Labour Cost",
  OVERHEAD_EXPENSE: "Overhead Expense",
  CAPITAL_INJECTION: "Capital Injection",
  CAPITAL_REPAYMENT: "Capital Repayment",
  ASSET_PURCHASE: "Asset Purchase",
  AMBASSADOR_COMMISSION: "Ambassador Commission",
  OWNER_DRAWING: "Owner Drawing",
  PROFIT_DISTRIBUTION: "Profit Distribution",
  REFUND: "Refund",
  CUSTOMER_REFUND_LIABILITY: "Refundable Customer Funds",
  CUSTOMER_REFUND_SETTLEMENT: "Refund Settlement",
  CUSTOMER_CREDIT_TRANSFER: "Customer Credit Transfer",
};

function Ledger({ showAlert }) {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ types: [], categories: [] });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [summary, setSummary] = useState(null);

  const PER = 20;
  const isMounted = useRef(true);

  useEffect(() => {
    api.getLedgerFilters().then(setFilterOptions).catch(() => {});
    api.getLedgerSummary().then(setSummary).catch(() => {});
  }, []);

  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;

  const refreshDataSilently = useCallback(async () => {
    if (!isMounted.current) return;
    try {
      const result = await api.getLedger({ page, limit: PER, ...appliedFilters });
      if (isMounted.current) {
        setEntries(result.entries || []);
        setTotal(result.total || 0);
        setError(null);
      }
    } catch(e) {
      console.error("Ledger silent refresh failed:", e);
    }
  }, [page, appliedFilters]);

  const initialLoad = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getLedger({ page, limit: PER, ...appliedFilters });
      if (isMounted.current) {
        setEntries(result.entries || []);
        setTotal(result.total || 0);
      }
    } catch(e) {
      console.error("Ledger error:", e);
      setError(e.message || "Failed to load ledger");
    }
    setLoading(false);
    setIsInitialLoad(false);
  }, [page, appliedFilters]);

  useEffect(() => {
    isMounted.current = true;
    initialLoad();
    return () => { isMounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  useEffect(() => {
    if (isInitialLoad) return;
    refreshDataSilently();
  }, [page, isInitialLoad, refreshDataSilently]);

  const handlePageChange = (newPage) => setPage(newPage);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  if (loading && isInitialLoad) {
    return <div className="spinner">Loading ledger...</div>;
  }

  if (error && entries.length === 0 && activeFilterCount === 0) {
    return (
      <div className="card">
        <div style={{ textAlign: "center", padding: 48, color: "var(--text-3)" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
          <div>Failed to load ledger</div>
          <div style={{ fontSize: 12, marginTop: 8 }}>{error}</div>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={initialLoad}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="section-header">
        <h2>📒 Ledger ({total})</h2>
        <button className="btn-sm btn-secondary" onClick={refreshDataSilently} style={{ fontSize: 11 }}>
          ↻ Refresh
        </button>
      </div>

      {/* ── Summary strip — the exact same computeFinancialSummary
          numbers the Dashboard reads (financialSummaryV2 → ledgerSummary),
          so there's nothing to reconcile between this screen and that
          one; they're the same query. ── */}
      {summary && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16,
        }}>
          {[
            { label: "Earned Revenue", value: summary.revenue, color: "var(--accent)" },
            { label: "Refundable / Customer-Held", value: Number(summary.refundableCustomerFunds || 0), color: "var(--gold)" },
            { label: "Cost", value: summary.cost, color: "var(--red)" },
            { label: "Net Profit", value: summary.netProfit, color: summary.netProfit >= 0 ? "var(--accent)" : "var(--red)" },
            { label: "Liquid Cash", value: summary.liquidCash, color: "#10B981" },
          ].map(item => (
            <div key={item.label} style={{
              flex: "1 1 140px", padding: "12px 14px", background: "var(--surface2)", borderRadius: 10,
            }}>
              <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
                {item.label}
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: item.color }}>{fmt(item.value)}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end",
        padding: "14px 16px", background: "var(--surface2)", borderRadius: 10, marginBottom: 16,
      }}>
        <div>
          <label style={labelStyle}>Search</label>
          <input type="text" placeholder="Description, reference..." value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} style={{ ...inputStyle, minWidth: 180 }} />
        </div>
        <div>
          <label style={labelStyle}>From</label>
          <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>To</label>
          <input type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Type</label>
          <select value={filters.type} onChange={e => setFilters(f => ({ ...f, type: e.target.value }))} style={{ ...inputStyle, minWidth: 170 }}>
            <option value="">All types</option>
            {filterOptions.types.map(t => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Category</label>
          <select value={filters.category} onChange={e => setFilters(f => ({ ...f, category: e.target.value }))} style={{ ...inputStyle, minWidth: 150 }}>
            <option value="">All categories</option>
            {filterOptions.categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button className="btn btn-primary btn-sm" onClick={applyFilters}>Apply</button>
        {activeFilterCount > 0 && (
          <button className="btn-sm btn-secondary" onClick={clearFilters}>✕ Clear filters ({activeFilterCount})</button>
        )}
      </div>

      <div className="table-wrap">
        <table className="w-full">
          <thead>
            <tr>
              <th>Time</th>
              <th>Type</th>
              <th>Category</th>
              <th>Description</th>
              <th>Reference</th>
              <th style={{ textAlign: "right" }}>Amount</th>
              <th>Recorded By</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: "center", color: "var(--text-3)", padding: 24 }}>
                {activeFilterCount > 0 ? "No entries match these filters." : "No entries yet."}
              </td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.ledgerId}>
                <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{new Date(e.recordedAt).toLocaleString()}</td>
                <td>
                  <span className={`badge ${hasCashImpact(e) ? (e.amount >= 0 ? "badge-active" : "badge-rejected") : ""}`}>
                    {TYPE_LABELS[e.type] || e.type}
                  </span>
                </td>
                <td style={{ fontSize: 11 }}>{e.category}</td>
                <td style={{ fontSize: 11.5 }}>{e.description}</td>
                <td style={{ fontFamily: "monospace", fontSize: 11 }}>{e.reference || "—"}</td>
                <td style={{ textAlign: "right", fontWeight: 700, fontSize: 12.5, color: hasCashImpact(e) ? (e.amount >= 0 ? "#10B981" : "var(--red)") : "var(--text)" }}>
                  {hasCashImpact(e) ? <>{e.amount >= 0 ? "+" : ""}{fmt(e.amount)}</> : fmt(Math.abs(e.amount))}
                </td>
                <td style={{ fontSize: 11 }}>{e.recordedBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: "12px 16px 0 16px", borderTop: "1px solid var(--border)" }}>
        <Pager page={page} total={total} per={PER} onChange={handlePageChange} />
      </div>
    </div>
  );
}

export default Ledger;
